import { useEffect, useState } from 'react';
import uniqBy from 'lodash/uniqBy';
import { COLUMNS, RIGHT_COLUMNS } from './AudioTableConsts';
import { Column, TableColumnWidthInfo } from '@devexpress/dx-react-grid';

const DEFAULT_COLUMN_WIDTH = 90;
const AUDIO_COLUMN_WIDTH = 468;
const AUDIO_COLUMN_MULTI_EXPS_WIDTH = 250;
const ACTION_COLUMN_WIDTH = 45;

type useAudioTableColumnWidthsProps = {
  columns: Column[];
  hasMultipleExperiments: boolean;
  containerWidth: number | undefined;
};

const COLUMNS_PADDING = 80;
const useAudioTableColumnWidths = ({
  columns,
  hasMultipleExperiments,
  containerWidth
}: useAudioTableColumnWidthsProps) => {
  const audioColumnWidth = hasMultipleExperiments
    ? AUDIO_COLUMN_MULTI_EXPS_WIDTH
    : AUDIO_COLUMN_WIDTH;

  const getWidths = () => {
    if (!columns.length || !containerWidth) return [];

    // Identify the last left-side column (before RIGHT_COLUMNS start)
    const lastLeftSideColumnIndex = columns.length - 1 - RIGHT_COLUMNS.length;
    if (lastLeftSideColumnIndex < 0) return [];

    const lastColumnName = columns[lastLeftSideColumnIndex].name;

    // Calculate fixed width (Action + Audio + Default columns excluding last)
    const fixedWidth =
      ACTION_COLUMN_WIDTH +
      audioColumnWidth -
      COLUMNS_PADDING +
      (columns.length - 2) * DEFAULT_COLUMN_WIDTH;

    const lastColumnWidth = Math.max(
      containerWidth - fixedWidth,
      DEFAULT_COLUMN_WIDTH
    );

    return uniqBy(
      [
        { columnName: COLUMNS.AUDIO, width: audioColumnWidth },
        { columnName: COLUMNS.ACTIONS, width: ACTION_COLUMN_WIDTH },
        ...columns.map(col => ({
          columnName: col.name,
          width:
            col.name === lastColumnName ? lastColumnWidth : DEFAULT_COLUMN_WIDTH
        }))
      ],
      'columnName'
    );
  };

  const [columnWidths, setColumnWidths] = useState<TableColumnWidthInfo[]>([]);

  useEffect(() => {
    setColumnWidths(prevWidths => {
      // Preserve previous column widths if they exist
      const newWidths = getWidths();
      if (!prevWidths) return newWidths;

      return newWidths.map(newWidth => {
        const existing = prevWidths.find(
          pw => pw.columnName === newWidth.columnName
        );
        return existing || newWidth;
      });
    });
  }, [columns, containerWidth]);

  return [columnWidths, setColumnWidths];
};

export default useAudioTableColumnWidths;
